// Copied, not imported, from the API package's realtime-protocol.ts: the same contract in two files, and changing one means changing both.
export const REALTIME_PROTOCOL_VERSION = 1;

export interface IRealtimeHello {
  type: 'hello';
  protocolVersion: number;
  resumeFrom?: string;
}

export interface IRealtimePong {
  type: 'pong';
  ts: number;
}

export type TRealtimeClientFrame = IRealtimeHello | IRealtimePong;

export interface IRealtimeReady {
  type: 'ready';
  sessionId: string;
  heartbeatSec: number;
  serverVersion: string;
  organizationId: string;
  backlog: number;
}

export interface IRealtimeEventCreated {
  type: 'event.created';
  event: {
    id: string;
    at: string;
    connectionId: string;
    sourceId: string;
    destinationId: string;
    eventType: string | null;
    status: string;
    generation: number;
    origin: string;
    maxAttempts: number;
  };
  catchup: boolean;
}

// A copy walking a flow; ids are the API's display ids (`evt_…`, `run_…`), as in `GET /v1/runs`.
export interface IRealtimeFlowCopyCreated {
  type: 'flow_copy.created';
  copy: {
    id: string;
    at: string;
    runId: string;
    flowId: string;
    flowVersionId: string;
    nodeKey: string;
    lineage: string;
    parentId: string | null;
    sourceId: string | null;
    eventType: string | null;
    status: string;
    generation: number;
  };
  catchup: boolean;
}

// A listed copy moved on; a signal, not the row: refetch its run.
export interface IRealtimeFlowCopyUpdated {
  type: 'flow_copy.updated';
  copy: {
    id: string;
    at: string;
    runId: string;
    flowId: string;
    nodeKey: string;
    status: string;
    lane: string | null;
  };
}

// A run's open count changed; `finished` once it has its final status.
export interface IRealtimeFlowRunUpdated {
  type: 'flow_run.updated';
  run: {
    id: string;
    at: string;
    flowId: string;
    copiesOpen: number;
    finished: boolean;
  };
}

export interface IRealtimeAgentStepUpdated {
  type: 'agent_step.updated';
  agent: {
    sessionId: string;
    flowId: string;
    nodeKey: string;
    runId: string | null;
    eventId: string | null;
    seq: number;
    status: string;
    sessionState: string;
  };
}

export interface IRealtimeAgentTestUpdated {
  type: 'agent_test.updated';
  testSessionId: string;
  seq: number;
  status: string;
  sessionState: string;
}

// Copy frames over their budget were dropped: flow lists refetch, the event list does not.
export interface IRealtimeFlowCopiesSkipped {
  type: 'flow_copy.skipped';
}

export interface IRealtimeCaughtUp {
  type: 'caught_up';
}

// Every count is optional on purpose: one the server did not compute means "no badge", never zero.
export interface IRealtimeCounters {
  type: 'counters';
  counters: {
    issues?: number;
    sources?: number;
    connections?: number;
    bookmarks?: number;
    queueDepth?: number;
  };
}

export interface IRealtimeBulkOperationUpdated {
  type: 'bulk_operation.updated';
  operation: {
    id: string;
    kind: string;
    state: string;
    total: number | null;
    processed: number;
    succeeded: number;
    failed: number;
    current: string | null;
    etaMs: number | null;
  };
}

export interface IRealtimeResync {
  type: 'resync';
  reason: 'resume_window_exceeded' | 'rate_limited';
}

export interface IRealtimePing {
  type: 'ping';
  ts: number;
}

export type TRealtimeErrorCode =
  'unauthorized' | 'forbidden' | 'version_unsupported' | 'rate_limited';

export interface IRealtimeError {
  type: 'error';
  code: TRealtimeErrorCode;
  message: string;
  fatal: boolean;
}

export interface IRealtimeCustomDomainChanged {
  type: 'custom_domain.changed';
  domainId: string;
}

export interface IRealtimeFlowDraftUpdated {
  type: 'flow_draft.updated';
  flowId: string;
  draftRevision: number;
  updatedBy: string | null;
  reason: 'saved' | 'discarded' | 'restored' | 'published';
}

export type TRealtimeServerFrame =
  | IRealtimeReady
  | IRealtimeEventCreated
  | IRealtimeFlowCopyCreated
  | IRealtimeFlowCopyUpdated
  | IRealtimeFlowRunUpdated
  | IRealtimeFlowCopiesSkipped
  | IRealtimeCaughtUp
  | IRealtimeCounters
  | IRealtimeCustomDomainChanged
  | IRealtimeBulkOperationUpdated
  | IRealtimeFlowDraftUpdated
  | IRealtimeAgentStepUpdated
  | IRealtimeAgentTestUpdated
  | IRealtimeResync
  | IRealtimePing
  | IRealtimeError;

export type TRealtimeFrameType = TRealtimeServerFrame['type'];

export type TRealtimeFrameOf<K extends TRealtimeFrameType> = Extract<
  TRealtimeServerFrame,
  { type: K }
>;
